/**
 * HistoryManager - Manages undo/redo history with view updates.
 *
 * Wraps CommandHistory and coordinates view updates and selection
 * grid redraws after undo/redo operations.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { CommandHistory } from "../commands/CommandHistory";
import type { ICommand } from "../commands/ICommand";
import type { DraggerManager } from "../draggers/DraggerManager";
import { selectionService } from "../selection";

export class HistoryManager {
  constructor(
    private viewer: DRDrawingViewer,
    private history: CommandHistory,
    private draggerManager: DraggerManager | null,
  ) {}

  /**
   * Undo the last command.
   *
   * Gives the active dragger first refusal via handleUndo() — a dragger
   * with in-progress multi-step state (e.g. SequentialDragger placing
   * polyline points) needs to undo its own last step and keep its
   * point-tracking arrays in sync with the command stack itself, rather
   * than this method popping CommandHistory out from under it. Without
   * this, a dragger-owned keydown handler and this central undo path could
   * both react to the same Ctrl+Z, undoing two commands while the dragger
   * only pops one point of internal state — see SequentialDragger.handleUndo().
   *
   * @returns true if undo was performed
   */
  undo(): boolean {
    if (this.draggerManager?.current?.handleUndo()) {
      selectionService.updateGrid();
      return true;
    }
    const result = this.history.undo();
    this.viewer.update();
    selectionService.updateGrid();
    return result;
  }

  /**
   * Redo the last undone command.
   *
   * Symmetric to undo() above: gives the active dragger first refusal via
   * handleRedo(), so a dragger that undid one of its own points (and is
   * tracking it for redo) re-applies it through its own point-tracking
   * instead of only the command stack advancing while the dragger's
   * worldPoints/commandCount silently stay one point behind — see
   * SequentialDragger.handleRedo().
   *
   * @returns true if redo was performed
   */
  redo(): boolean {
    if (this.draggerManager?.current?.handleRedo()) {
      selectionService.updateGrid();
      return true;
    }
    const result = this.history.redo();
    this.viewer.update();
    selectionService.updateGrid();
    return result;
  }

  /**
   * Check if undo is available.
   */
  get canUndo(): boolean {
    return this.history.canUndo;
  }

  /**
   * Check if redo is available.
   */
  get canRedo(): boolean {
    return this.history.canRedo;
  }

  /**
   * Execute a command and add to history.
   */
  execute(command: ICommand): void {
    this.history.execute(command);
  }

  /**
   * Subscribe to history changes.
   */
  onChange(handler: () => void): void {
    this.history.on("change", handler);
  }

  /**
   * Unsubscribe from history changes.
   */
  offChange(handler: () => void): void {
    this.history.off("change", handler);
  }

  /**
   * Mark the drawing as dirty without adding a command to the history stack.
   * Use this for operations that modify the file but bypass the command system
   * (e.g. layout management).
   */
  markDirty(): void {
    this.history.markDirty();
  }
}
